import { createRef, h } from "preact";
import { useEffect, useState } from "preact/hooks";
import sortBy from "lodash/sortBy";

import { uniqueID } from "../../helpers.js";
import i18n from "../../i18n.js";

import AutoSuggest from "../AutoSuggest.jsx";
import Icon from "../Icon.jsx";
import Proficiency from "./Proficiency.jsx";
import Slider from "../Slider.jsx";

function createChoices(suggestions) {
  return suggestions.map(([text, _normalized]) => ({ text }));
}

function createProficiencies(items) {
  return items.filter((x) => x).map(([text, value]) => ({ text, value }));
}

export default function SkillChooser(props) {
  const [choices, setChoices] = useState([]);
  const [visibleChoices, setVisibleChoices] = useState([]);
  const [value, setValue] = useState("");
  const [id, setId] = useState(uniqueID());
  const [error, setError] = useState(null);
  const [currentValues, setCurrentValues] = useState(
    Array.isArray(props.answer) ? props.answer : []
  );
  const [disabled, setDisabled] = useState(false);
  const ref = createRef();

  const isLikert = !!props.options.likert;
  const proficiencies =
    !isLikert && createProficiencies(props.options.proficiencies);

  function handleAddItem({ text }) {
    const value = text;
    const newValue = choices.find(({ text }) =>
      text.toLowerCase() == value.toLowerCase() ? text : null
    ).text;
    if (newValue) {
      addValue(newValue);
      setId(uniqueID());
    } else {
      setError(i18n.t("component.skill_chooser.wrong_value"));
    }
  }

  function setKeyValue(key, value, index) {
    const newValues = currentValues.map((item, i) =>
      i === index ? { ...item, [key]: value } : item
    );

    setCurrentValues(newValues);
    props.onSelect(
      newValues.map(({ value, proficiency }) => ({
        value: pickChoice(value),
        proficiency,
      })),
      null,
      checkValidity(newValues)
    );
  }

  function checkValidity(newValues) {
    if (newValues.length === 0) {
      return i18n.t("component.skill_chooser.wrong_value");
    }
    if (newValues.find(({ proficiency }) => !proficiency)) {
      return i18n.t("component.skill_chooser.wrong_proficiency");
    }
    return null;
  }

  function addValue(value) {
    if (currentValues.indexOf(value) === -1) {
      const newValues = [
        ...currentValues,
        isLikert ? { value, proficiency: 1 } : { value },
      ];
      setCurrentValues(newValues);
      props.onSelect(newValues, null, checkValidity(newValues));
    }
  }

  function pickChoice(value) {
    if (props.options.choices) {
      return (
        props.options.choices.find(([text, _val]) => text === value) || value
      );
    }
    return value;
  }

  function handleNew(event) {
    setError(null);
    setValue(typeof event === "string" ? event : event.target.value);
  }

  function handleProficiencyChange(event, index) {
    setKeyValue(
      "proficiency",
      typeof event === "string" ? event : event.target.value,
      index
    );
  }

  function handleLikertChange(event, index) {
    setKeyValue(
      "proficiency",
      typeof event === "number" ? event : event.target.value,
      index
    );
  }

  function removeItem(index) {
    const newValues = [
      ...currentValues.slice(0, index),
      ...currentValues.slice(index + 1),
    ];
    setCurrentValues(newValues);
    props.onSelect(newValues, null, checkValidity(newValues));
  }

  useEffect(() => {
    setDisabled(props.disabled);
  }, [props.disabled]);

  useEffect(() => {
    const values = currentValues.map(({ value }) =>
      Array.isArray(value) ? value[0] : value
    );

    setVisibleChoices(
      sortBy(choices, (a) => a.text.toLowerCase()).filter(
        ({ text }) => !values.includes(text)
      )
    );
  }, [choices, currentValues]);

  useEffect(() => {
    if (props.options.choices && props.options.choices.length > 0) {
      setChoices(createChoices(props.options.choices));
    }
  }, []);

  useEffect(() => {
    // Not quite sure why this indirection is needed, but without it,
    // the value is never really changed.
    setValue("");
  }, [id]);
  return (
    <div class="mb-32 relative" ref={ref}>
      <AutoSuggest
        showCaret
        suggestions={visibleChoices}
        onChange={(event) => handleNew(event)}
        onSelect={handleAddItem}
        value={value}
        key={id.toString()}
        inputProps={{
          id: `question-skill-new`,
          name: `answer`,
          autocomplete: "off",
          info: props.options.helperText,
          error: !!(props.error || error),
          label: props.options.label,
          disabled:
            disabled ||
            (props.options.limit &&
              currentValues.length >= props.options.limit),
        }}
      />
      {currentValues.map(({ value, proficiency }, i) => {
        // Value is prefilled
        if (Array.isArray(value)) {
          value = value[0];
        }
        const selectedProficiency = isLikert
          ? proficiency || 1
          : proficiency &&
            proficiencies.find((item) => item.value === proficiency)?.text;
        return (
          <div class="skill-chooser__row">
            <div class="skill-chooser__col skill-chooser__col--skill">
              {value}
            </div>
            <div class="skill-chooser__col skill-chooser__col--prof">
              {isLikert ? (
                <Slider
                  class="skill-chooser__slider"
                  min={1}
                  max={props.options.likert}
                  value={proficiency}
                  label="TEST"
                  getTooltip={(i) =>
                    i18n.t(`${props.options.valueI18nPrefix}${i}`)
                  }
                  onChange={(event) => handleLikertChange(event, i)}
                />
              ) : (
                <Proficiency
                  proficiencies={proficiencies}
                  onClick={(event) => handleProficiencyChange(event, i)}
                  selectedProficiency={selectedProficiency}
                  error={props.error || error}
                />
              )}
              <Icon
                class="skill-chooser__dropdown-icon"
                onClick={(event) => removeItem(i)}
              >
                delete
              </Icon>
            </div>
          </div>
        );
      })}
      {props.error && <span class="skill-chooser__error">{props.error}</span>}
    </div>
  );
}
